import * as React from 'react';
import humanizeUtil from '@utils/humanizeUtil';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import {
  GaugeContainer,
  GaugeValue,
  GaugeChartWrapper,
  GaugeTitle,
  ThresholdContainer,
  MetricItem,
  GaugeContentWrapper,
  GaugeLeft,
} from './styled';
import { getThresholds, getTextColor, formatGaugeChartData } from './utils';
import { Tooltip } from 'antd';
import { FLOW_METRICS_DISPLAY } from '@src/utils/flow/meta';

export interface GaugeProps {
  value: number;
  description?: string;
  settings: {
    threshold: {
      t1?: number;
      t2?: number;
    };
    timeout?: number;
    timeout_unit?: string;
  };
}

const Gauge = ({ value, description, settings }: GaugeProps) => {
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const thresholds = React.useMemo(() => getThresholds(settings), [settings]);
  const scoreColor = getTextColor(value || 0, thresholds.absolute);
  const chartOption = React.useMemo(
    () => formatGaugeChartData(humanizeUtil(value, '').humanizedNumber, thresholds.scaled),
    [value, thresholds.scaled],
  );

  React.useEffect(() => {
    if (instance) {
      instance.setOption(chartOption);
    }
  }, [instance, chartOption]);

  return (
    <GaugeContainer>
      <GaugeLeft>
        <GaugeTitle>{FLOW_METRICS_DISPLAY.success.name} %</GaugeTitle>
      </GaugeLeft>
      <GaugeContentWrapper>
        <GaugeChartWrapper>
          <BaseChart chartInstanceRefCb={chartInstanceRefCb} style={{ width: 60, height: 40 }} />
          <Tooltip title={description || ''} placement="top">
            <GaugeValue style={{ color: scoreColor }}>
              {humanizeUtil(value, '').humanizedNumber} %
            </GaugeValue>
          </Tooltip>
        </GaugeChartWrapper>
        <ThresholdContainer>
          <div className="top">
            <MetricItem>
              <span style={{ color: '#52c41a' }}>Good:</span>
              <span className="value">&gt; {settings.threshold.t2}%</span>
            </MetricItem>
            <MetricItem>
              <span style={{ color: '#faad14' }}>Fair:</span>
              <span className="value">
                {settings.threshold.t1}% - {settings.threshold.t2}%
              </span>
            </MetricItem>
            <MetricItem>
              <span style={{ color: '#ff4d4f' }}>Poor:</span>
              <span className="value">&lt; {settings.threshold.t1}%</span>
            </MetricItem>
          </div>
          <div className="bottom">
            <MetricItem>
              Flow Success:
              <span className="value">
                {settings.timeout} {settings.timeout_unit}
              </span>
            </MetricItem>
          </div>
        </ThresholdContainer>
      </GaugeContentWrapper>
    </GaugeContainer>
  );
};

export default Gauge;
